const mimeTypeMap: Record<string, string> = {
  csv: 'text/csv',
  txt: 'text/plain',
  json: 'application/json',
};

/**
 * Infers MIME type from filename extension
 */
const inferMimeType = (filename: string): string => {
  const extension = filename.split('.').pop()?.toLowerCase();

  return (extension && mimeTypeMap[extension]) || 'application/octet-stream';
};

/**
 * Downloads a file using native browser APIs.
 * Creates a temporary anchor element with download attribute and triggers click.
 *
 * @param data - String or Blob data to download
 * @param filename - Filename for the downloaded file
 * @param mimeType - Optional MIME type (inferred from filename if not provided)
 */
export const downloadFile = (
  data: string | Blob,
  filename: string,
  mimeType?: string
): void => {
  // Create Blob from data
  let blob: Blob;

  if (data instanceof Blob) {
    blob = data;
  } else {
    const finalMimeType = mimeType || inferMimeType(filename);
    blob = new Blob([data], { type: finalMimeType });
  }

  // Create object URL and anchor element
  const blobURL = URL.createObjectURL(blob);
  const tempLink = document.createElement('a');
  tempLink.style.display = 'none';
  tempLink.href = blobURL;
  tempLink.setAttribute('download', filename);
  // This prevents event bubbling which can cause drawers and modals to close when a download is triggered.
  // Normally, we don't want this to propagate an event, but if this ever causes issues, we can add
  // a configuration option to disable this behavior.
  tempLink.onclick = (e): void => e.stopPropagation();

  // Trigger download
  document.body.appendChild(tempLink);
  tempLink.click();

  // Cleanup after 200ms delay (allows download to start)
  setTimeout(() => {
    document.body.removeChild(tempLink);
    URL.revokeObjectURL(blobURL);
  }, 200);
};
